import {
  Grid,
  Typography,
  Paper,
  TextField,
  Checkbox,
  Button,
  FormControlLabel,
  Link,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Logo from "assets/icons/logo.svg";
import CoverImage from "assets/icons/cover.svg";
import { useNavigate } from "react-router-dom";
import { setToken } from "services/tokenUtils";
import { useUser } from "contexts/UserContext";
import { useState } from "react";

const schema = z.object({
  email: z.string().email("You must enter a valid email"),
  password: z
    .string()
    .min(4, "Password is too short - must be at least 4 chars."),
});

const defaultValues = {
  email: "",
  password: "",
  remember: true,
};

function SignInPage() {
  const { control, formState, handleSubmit } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { errors } = formState;

  const { login } = useUser();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(null);

  const onSubmit = async (data) => {
    try {
      setErrorMessage(null); 
      const response = await login(data.email, data.password);
      
          if (!response || !response.token) {
        throw new Error("No token received from login()");
      }
  
      setToken(response.token);
      navigate("/dashboard");
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || "Login failed. Please try again."
      );
    }
  };

  return (
    <Grid container sx={{ height: "100vh" }}>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "2rem",
        }}
      >
        <Paper
          elevation={10}
          sx={{
            padding: "2rem",
            borderRadius: "1rem",
            width: "100%",
            maxWidth: "400px",
            textAlign: "left",
          }}
        >
          <img
            src={Logo}
            alt="logo"
            style={{
              marginBottom: "1.5rem",
              width: "rem",
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              color: "text.dark",
              marginBottom: "1.5rem",
            }}
          >
            Sign in
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  error={!!errors.email}
                  helperText={errors?.email?.message}
                  variant="outlined"
                  required
                  fullWidth
                  sx={{ marginBottom: "1rem", color: "text.dark" }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password"
                  type="password"
                  error={!!errors.password}
                  helperText={errors?.password?.message}
                  variant="outlined"
                  required
                  fullWidth
                  sx={{ marginBottom: "1rem" }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />

            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              sx={{ marginBottom: "1.5rem" }}
            >
              {errorMessage && (
                <Typography color="error" sx={{ marginBottom: "1rem" }}>
                  {errorMessage}
                </Typography>
              )}
              <Controller
                name="remember"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} size="small" />}
                    label="Remember me"
                  />
                )}
              />
              <Link href="/forgot-password" sx={{ fontSize: "0.9rem" }}>
                Forgot password?
              </Link>
            </Grid>

            <Button
              variant="contained"
              type="submit"
              fullWidth
              sx={{
                color: "",
                height: "50px",
                fontWeight: "bold",
                borderRadius: "8px",
              }}
            >
              Sign In
            </Button>
          </form>
        </Paper>
      </Grid>

      <Grid
        item
        xs={false}
        md={6}
        sx={{
          display: { xs: "none", md: "block" },
          backgroundImage: `url(${CoverImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
    </Grid>
  );
}

export default SignInPage;
